import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Button, MenuItem, Paper, Stack, Chip, Box, TextField, Typography,
} from '@mui/material';
import { useOperations } from './OperationsProvider';
import { areasOf, areaTypeLabel } from './areas';

export const day = (date) => { const d = new Date(date); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
// A shift does not start at midnight. Asking for the night shift, or for the hour either side of a
// weighbridge dispute, means saying the time as well as the day - so the range holds both, and a
// link written when it held dates alone still means the whole of those days.
export const startOf = (value) => (value ? new Date(value.includes('T') ? value : `${value}T00:00:00`) : null);
export const endOf = (value) => {
  if (!value) return null;
  if (value.includes('T')) return new Date(`${value}${value.length === 16 ? ':59.999' : ''}`);
  const midnight = new Date(`${value}T00:00:00`); midnight.setDate(midnight.getDate() + 1);
  return new Date(midnight.getTime() - 1);
};
export const inDates = (value, from, to) => {
  const time = Date.parse(value); const start = startOf(from); const end = endOf(to);
  return Number.isFinite(time) && (!start || time >= start.getTime()) && (!end || time <= end.getTime());
};
// The same range as the server reads it: an instant to start at, and the instant it stops before.
export const rangeUtc = (from, to) => {
  const start = startOf(from); const end = endOf(to);
  return { fromUtc: start ? start.toISOString() : '', toUtc: end ? new Date(end.getTime() + 1).toISOString() : '' };
};
const withClock = (value, clock) => (!value || value.includes('T') ? value : `${value}T${clock}`);
export const useScope = () => {
  const [params, setParams] = useSearchParams();
  const from = withClock(params.get('from') ?? day(Date.now() - 6 * 86400000), '00:00');
  const to = withClock(params.get('to') ?? day(Date.now()), '23:59');
  const { data } = useOperations();
  const scope = {
    from,
    to,
    site: params.get('site') || '',
    route: params.get('route') || '',
    vehicle: params.get('vehicle') || '',
    movement: params.get('movement') || '',
    direction: params.get('direction') || '',
    area: params.get('area') || '',
    // What the load is, who carries it, where it came from and where it is going - the four things
    // a plant asks about its own traffic, and the four the dashboard could not be asked for.
    transporter: params.get('transporter') || '',
    material: params.get('material') || '',
    origin: params.get('origin') || '',
    destination: params.get('destination') || '',
  };
  // Built from the current params rather than passed as an updater function: the
  // function form of setSearchParams arrived in react-router 6.4, and on 6.3 it is
  // handed to createSearchParams, which silently yields nothing. Every filter was a
  // no-op as a result.
  const set = (key, value) => { const next = new URLSearchParams(params); next.set(key, value); setParams(next); };
  const invalid = !!(from && to && from > to);
  // Area is matched against where the vehicle is standing now, not against anything
  // recorded on the trip — it is a live location filter, so it deliberately ignores the
  // date range in the same way the map does.
  const inArea = (trip) => !scope.area || areasOf(data.areas, trip.vehicleId).some((a) => String(a.id) === scope.area);
  // Words typed at a counter vary in case and spacing; the same consignor is still the same consignor.
  const same = (left, right) => !right || String(left || '').trim().toLowerCase() === right.trim().toLowerCase();
  const matches = (trip, withDate = true) => !invalid && (!withDate || inDates(trip.createdAt, from, to)) && (!scope.site || trip.checkpoints?.some((c) => c.site === scope.site)) && (!scope.route || String(trip.routeId) === scope.route) && (!scope.vehicle || String(trip.vehicleId) === scope.vehicle) && (!scope.movement || trip.movementType === scope.movement) && (!scope.direction || trip.direction === scope.direction) && same(trip.transporter, scope.transporter) && same(trip.material, scope.material) && same(trip.consignor, scope.origin) && same(trip.destination, scope.destination) && inArea(trip);
  const allDates = () => { const next = new URLSearchParams(params); next.set('from', ''); next.set('to', ''); setParams(next); };
  const clear = () => { const next = new URLSearchParams(params); Object.keys(scope).forEach((key) => next.delete(key)); setParams(next); };
  return { ...scope, set, invalid, matches, allDates, clear };
};
const ScopeFilters = ({ scope, dateLabel = 'Trip creation date', showArea = true }) => {
  const { data, trips } = useOperations();
  // What the plant has actually hauled, offered as the filter: the list it keeps, plus anything on a
  // trip that is not on the list yet, so a value can always be selected back out of the data.
  const words = (list, of) => [...new Set([
    ...(data.catalog || []).filter((c) => c.list === list).map((c) => c.name),
    ...(trips || []).map(of).filter(Boolean),
  ])].sort((a, b) => a.localeCompare(b)).map((name) => [name, name]);
  const select = (key, label, options) => (
    <TextField select key={key} label={label} value={scope[key]} onChange={(e) => scope.set(key, String(e.target.value))} sx={{ minWidth: 150, flex: '1 1 150px' }}>
      <MenuItem value="">All</MenuItem>
      {options.map(([value, title]) => <MenuItem key={value} value={String(value)}>{title}</MenuItem>)}
    </TextField>
  );
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="caption" color="text.secondary">{`${dateLabel} · ${Intl.DateTimeFormat().resolvedOptions().timeZone} · Date filters use this timezone`}</Typography>
      <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mt: 1 }}>
        {['from', 'to'].map((key) => <TextField key={key} label={key === 'from' ? 'From' : 'To'} type="datetime-local" InputLabelProps={{ shrink: true }} value={scope[key]} onChange={(e) => scope.set(key, e.target.value)} sx={{ flex: '1 1 210px' }} />)}
        <Button onClick={scope.allDates}>All dates</Button>
        <Button onClick={scope.clear}>Clear all</Button>
      </Stack>
      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>{['site', 'route', 'vehicle', 'movement', 'direction', 'area', 'transporter', 'material', 'origin', 'destination'].filter((key) => scope[key]).map((key) => <Chip key={key} label={`${key}: ${scope[key]}`} onDelete={() => scope.set(key, '')} />)}</Stack>
      <Box component="details" sx={{ mt: 1 }}>
        <Typography component="summary" sx={{ cursor: 'pointer' }}>Advanced filters</Typography>
        <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mt: 2 }}>
          {select('site', 'Site', [...new Set(data.checkpoints.map((c) => c.site))].map((s) => [s, s]))}
          {select('route', 'Route', data.routes.map((r) => [r.id, r.name]))}
          {select('vehicle', 'Vehicle', data.vehicles.map((v) => [v.id, v.name]))}
          {select('movement', 'Movement', [['LOAD', 'Load'], ['UNLOAD', 'Unload']])}
          {select('direction', 'Direction', [['INBOUND', 'Inbound'], ['OUTBOUND', 'Outbound']])}
          {select('transporter', 'Transporter', words('transporter', (t) => t.transporter))}
          {select('material', 'Material', words('material', (t) => t.material))}
          {select('origin', 'Origin', words('consignor', (t) => t.consignor))}
          {select('destination', 'Destination', words('destination', (t) => t.destination))}
          {showArea && select('area', 'Area', (data.areas || []).map((a) => [a.id, `${a.name} · ${areaTypeLabel(a.type)}`]))}
        </Stack>
      </Box>
      {scope.invalid && <Alert severity="error" sx={{ mt: 1 }}>From date must not be after To date.</Alert>}
    </Paper>
  );
};
export default ScopeFilters;
